import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { Me } from "../../api/client.js";
import type { PhoneMenuProps } from "./types.js";
import "./phone.css";

interface Item {
  to: string;
  label: string;
}

/**
 * The places of the shell's rail, by who is signed in. Groups is its own page here: `/?groups=1` opens
 * the panel beside the grid, and on a phone the Plan page is the week instead.
 */
export function menuItems(me: Me, monday: string): Item[] {
  const items: Item[] = [
    { to: `/w/${monday}/plan`, label: "Plan" },
    { to: `/w/${monday}`, label: "Week" },
    { to: "/live", label: "Live" },
    { to: "/athletes", label: "Athletes" },
    { to: "/lifts", label: "Lifts" },
    { to: "/meets", label: "Meets" },
  ];
  if (me.person.canPlan || me.person.isAdmin) items.push({ to: "/groups", label: "Groups" });
  items.push({ to: "/settings", label: "Settings" });
  return items;
}

/** `Plan ▾` (§33.1): on a phone the shell's bar gives way on every page; its places are in this menu. */
export function PhoneMenu({ me, monday, onSignOut, current = "Plan", onSearch }: PhoneMenuProps) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLElement>("a, button")?.focus();
    const away = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    // Not the pointerdown that opened it.
    const id = setTimeout(() => window.addEventListener("pointerdown", away), 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener("pointerdown", away);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    button.current?.focus();
  };

  return (
    <div className="wp-menu-wrap" ref={wrap}>
      <button ref={button} type="button" className="wp-bar-btn wpw-plan" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {current}
        <svg className="wpw-caret" aria-hidden="true" viewBox="0 0 20 20" width="16" height="16">
          <path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="m5 7.5 5 5 5-5" />
        </svg>
      </button>
      {open ? (
        <div
          ref={menu}
          className="wp-menu is-start"
          role="menu"
          aria-label={current}
          onKeyDown={(e) => {
            const all = Array.from(menu.current?.querySelectorAll<HTMLElement>("a, button") ?? []);
            const i = all.indexOf(document.activeElement as HTMLElement);
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              all[(i + (e.key === "ArrowDown" ? 1 : -1) + all.length) % all.length]?.focus();
            } else if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              close();
            } else if (e.key === "Tab") setOpen(false);
          }}
        >
          {onSearch ? (
            <button
              type="button"
              role="menuitem"
              className="wp-menu-item"
              onClick={() => {
                setOpen(false);
                onSearch();
              }}
            >
              Go to…
            </button>
          ) : null}
          {menuItems(me, monday).filter((item) => item.label !== current).map((item) => (
            <Link key={item.label} role="menuitem" className="wp-menu-item" to={item.to} onClick={() => setOpen(false)}>
              {item.label}
            </Link>
          ))}
          <button
            type="button"
            role="menuitem"
            className="wp-menu-item is-split"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
